import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { tasksApi } from '../../../api/tasks.js';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const KINDS = [
  { value: 'todo', label: 'Attività' },
  { value: 'alert', label: 'Alert' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'logistics', label: 'Logistica' },
  { value: 'training', label: 'Formazione' }
];

const EMPTY = { kind: 'todo', title: '', description: '', priority: 'normal', dueAt: '', storeId: '' };

export function TaskFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();
  const storesQuery = useQuery({ queryKey: ['stores', 'all'], enabled: open, queryFn: () => storesApi.list({ limit: 100 }) });
  const stores = useMemo(() => ((storesQuery.data && storesQuery.data.items) || []), [storesQuery.data]);

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        kind: initial.kind || 'todo',
        title: initial.title || '',
        description: initial.description || '',
        priority: initial.priority || 'normal',
        dueAt: initial.dueAt ? new Date(initial.dueAt).toISOString().slice(0, 10) : '',
        storeId: String(initial.storeId?._id || initial.storeId || '')
      });
    } else {
      reset(EMPTY);
    }
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        kind: values.kind,
        title: values.title,
        description: values.description || undefined,
        priority: values.priority,
        dueAt: values.dueAt || null,
        storeId: values.storeId || null
      };
      return editing ? tasksApi.update(initial._id, payload) : tasksApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Attività aggiornata.' : 'Attività creata.');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica attività' : 'Nuova attività'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="task-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="task-form" className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Input label="Titolo" error={errors.title?.message} {...register('title', { required: 'Titolo richiesto' })} />
        <Textarea label="Descrizione" rows={3} {...register('description')} />
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select label="Tipo" {...register('kind')}>
            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
          <Select label="Priorità" {...register('priority')}>
            <option value="low">Bassa</option>
            <option value="normal">Normale</option>
            <option value="high">Alta</option>
          </Select>
          <Input label="Scadenza" type="date" {...register('dueAt')} />
        </section>
        <Select label="Negozio" {...register('storeId')}>
          <option value="">Generale</option>
          {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </Select>
      </form>
    </Modal>
  );
}
